import clsx from 'clsx/lite'
import React from 'react'
import { type HTMLAttributes, useEffect, useRef, useState } from 'react'

export type TabsProps = {
  items?: Array<{ label: string; value: string }>
  selectedItem?: string | undefined
  onChange?: (value: string) => void
} & Omit<HTMLAttributes<HTMLDivElement>, 'onChange'>

export const Tabs: React.FC<TabsProps> = ({
  items,
  onChange,
  selectedItem,
  className,
  ...props
}) => {
  const [activeTab, setActiveTab] = useState<string | undefined>(
    selectedItem || items?.[0]?.value
  )
  const containerRef = useRef<HTMLDivElement | undefined>(null)
  const tabRefs = useRef<Array<HTMLDivElement | undefined>>([])
  const [scrollToCenter, setScrollToCenter] = useState(false)

  const scrollToTabCenter = (tabElement: HTMLElement | undefined) => {
    if (!tabElement || !containerRef.current) return
    containerRef.current.scrollTo({
      left:
        tabElement.offsetLeft -
        (containerRef.current.offsetWidth - tabElement.offsetWidth) / 2,
      behavior: 'smooth',
    })
  }

  useEffect(() => {
    if (selectedItem) {
      handleTabClick(selectedItem)
    }
  }, [selectedItem])

  useEffect(() => {
    scrollToTabCenter(
      tabRefs.current[items?.findIndex((item) => item.value === activeTab) || 0]
    )
  }, [])

  useEffect(() => {
    if (!scrollToCenter) return
    scrollToTabCenter(
      tabRefs.current[items?.findIndex((item) => item.value === activeTab) || 0]
    )
    setScrollToCenter(false)
  }, [scrollToCenter, activeTab, items, selectedItem])

  const handleTabClick = (item: string) => {
    if (activeTab !== item) {
      setActiveTab(item)
      setScrollToCenter(true)
      onChange?.(item)
    }
  }

  return (
    <div
      className={clsx(
        'relative flex items-center overflow-x-auto border-b border-solid border-b-grey-200 [&::-webkit-scrollbar]:hidden',
        className
      )}
      {...props}
      ref={containerRef}
      role="tablist"
    >
      {items?.map((item, index) => (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/interactive-supports-focus
        <div
          key={index}
          ref={(el) => (tabRefs.current[index] = el)}
          className="group/tabs cursor-pointer [&:not(:first-of-type)]:pl-4 [&:not(:last-of-type)]:pr-4"
          role="tab"
          onClick={() => handleTabClick(item.value)}
        >
          <span
            className={clsx(
              'relative block pb-2.5 text-center group-hover/tabs:before:bg-green-500',
              'before:absolute before:bottom-0 before:left-0 before:h-[3px] before:w-full',
              item.value === activeTab ? 'font-medium before:bg-green-500' : ''
            )}
          >
            {item.label}
          </span>
        </div>
      ))}
    </div>
  )
}
